import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip } from '@mui/material';
import { People as PeopleIcon, Church as ChurchIcon, Star as StarIcon, SupportAgent as SupportIcon } from '@mui/icons-material';
import api from '../../utils/axios';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await api.get('/superadmin/analytics');
      if (res.data.success) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching admin stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, gradient }) => (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ 
        position: 'absolute', 
        top: -20, 
        right: -20, 
        width: 100, 
        height: 100, 
        borderRadius: '50%', 
        background: gradient,
        opacity: 0.1 
      }} />
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" fontWeight={600} gutterBottom>
              {title}
            </Typography>
            <Typography variant="h3" fontWeight={800} color="text.primary">
              {value}
            </Typography>
          </Box>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            background: gradient,
            color: 'white',
            display: 'flex',
            boxShadow: '0 4px 14px rgba(0,0,0,0.1)'
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Overview</Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Welcome to the Prelude Staff Dashboard.
      </Typography>

      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Users" 
            value={stats?.totalUsers || 0} 
            icon={<PeopleIcon />} 
            gradient="linear-gradient(135deg, #a259ff 0%, #7e3ed6 100%)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Churches" 
            value={stats?.totalChurches || 0} 
            icon={<ChurchIcon />} 
            gradient="linear-gradient(135deg, #00C9FF 0%, #92FE9D 100%)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="PRO Users" 
            value={stats?.totalProUsers || 0} 
            icon={<StarIcon />} 
            gradient="linear-gradient(135deg, #F9D423 0%, #FF4E50 100%)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Active Tickets" 
            value={stats?.activeTickets || 0} 
            icon={<SupportIcon />} 
            gradient="linear-gradient(135deg, #ff6b35 0%, #ff9e75 100%)"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>Músicas do Momento (Trending)</Typography>
          <Card>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Song</TableCell>
                    <TableCell>Artist</TableCell>
                    <TableCell align="right">Uses</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats?.trendySongs?.map((song) => (
                    <TableRow key={song._id}>
                      <TableCell sx={{ fontWeight: 600 }}>{song.title}</TableCell>
                      <TableCell>{song.artist}</TableCell>
                      <TableCell align="right">
                        <Chip label={song.usageCount} size="small" color="primary" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!stats?.trendySongs || stats.trendySongs.length === 0) && (
                    <TableRow><TableCell colSpan={3} align="center">No trending songs found.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>Músicas Indicadas (Staff Recommended)</Typography>
          <Card>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Song</TableCell>
                    <TableCell>Artist</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats?.recommendedSongs?.map((song) => (
                    <TableRow key={song._id}>
                      <TableCell sx={{ fontWeight: 600 }}>{song.title}</TableCell>
                      <TableCell>{song.artist}</TableCell>
                    </TableRow>
                  ))}
                  {(!stats?.recommendedSongs || stats.recommendedSongs.length === 0) && (
                    <TableRow><TableCell colSpan={2} align="center">No recommended songs found.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
